import { Fragment, useMemo, useState } from "react";
import { useAiRuns, useAiSources } from "@/hooks/useAiAgent";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ChevronDown, ChevronRight } from "lucide-react";
import { TableCard } from "@/components/ui/table-card";

const statusMap: Record<
  string,
  { label: string; variant: "info" | "success" | "neutral" | "danger" }
> = {
  pending: { label: "Oczekuje", variant: "neutral" },
  running: { label: "W trakcie", variant: "info" },
  completed: { label: "Zakończono", variant: "success" },
  failed: { label: "Błąd", variant: "danger" },
};

const STATUS_OPTIONS = [
  { value: "all", label: "Wszystkie statusy" },
  { value: "completed", label: "Zakończono" },
  { value: "running", label: "W trakcie" },
  { value: "pending", label: "Oczekuje" },
  { value: "failed", label: "Błąd" },
];

export default function AiRunsPage() {
  const { data: runs, isLoading } = useAiRuns();
  const { data: sources } = useAiSources();
  const [expandedRuns, setExpandedRuns] = useState<Set<string>>(new Set());
  const [statusFilter, setStatusFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");

  const filteredRuns = useMemo(() => {
    if (!runs) return runs;
    return runs.filter((r) => {
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      if (sourceFilter !== "all" && r.sourceId !== sourceFilter) return false;
      return true;
    });
  }, [runs, statusFilter, sourceFilter]);

  const toggleExpanded = (runId: string) => {
    setExpandedRuns((prev) => {
      const next = new Set(prev);
      if (next.has(runId)) next.delete(runId);
      else next.add(runId);
      return next;
    });
  };

  return (
    <div className="space-y-6">
      <h1 className="sr-only">Agent AI — Historia uruchomień</h1>
      <p className="text-pulso-muted">Przegląd wszystkich uruchomień agenta AI</p>

      {/* Filters */}
      <div className="flex gap-3 items-center">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {sources && sources.length > 0 && (
          <Select value={sourceFilter} onValueChange={setSourceFilter}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Źródło" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Wszystkie źródła</SelectItem>
              {sources.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {isLoading ? (
        <p className="text-muted-foreground">Ładowanie...</p>
      ) : !filteredRuns?.length ? (
        <div className="text-center py-12 text-muted-foreground">
          <p className="text-lg">Brak uruchomień</p>
          <p className="text-sm mt-1">
            {runs?.length
              ? "Brak uruchomień pasujących do filtrów"
              : "Agent nie był jeszcze uruchamiany"}
          </p>
        </div>
      ) : (
        <TableCard>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Data</TableHead>
                <TableHead>Źródło</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Znalezione</TableHead>
                <TableHead>Dodane</TableHead>
                <TableHead>Pominięte / do sprawdzenia</TableHead>
                <TableHead>Szczegóły</TableHead>
                <TableHead>Strony</TableHead>
                <TableHead>Tokeny</TableHead>
                <TableHead>Rozliczenie</TableHead>
                <TableHead>Błędy</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredRuns.map((run) => {
                const st = statusMap[run.status] ?? {
                  label: run.status,
                  variant: "neutral" as const,
                };
                const isExpanded = expandedRuns.has(run.id);
                const hasErrors = run.errors && run.errors.length > 0;
                const hasSkipped = run.skippedEvents && run.skippedEvents.length > 0;
                const hasDetails = hasErrors || hasSkipped;
                return (
                  <Fragment key={run.id}>
                    <TableRow>
                      <TableCell className="text-sm">
                        {run.startedAt
                          ? new Date(run.startedAt).toLocaleString("pl-PL")
                          : run.completedAt
                            ? new Date(run.completedAt).toLocaleString("pl-PL")
                            : "—"}
                      </TableCell>
                      <TableCell className="text-sm">
                        {run.sourceName ?? (
                          <span className="text-muted-foreground italic">Wszystkie</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={run.status === "completed" && hasErrors ? "warning" : st.variant}
                        >
                          {run.status === "completed" && hasErrors
                            ? "Zakończono z ograniczeniami"
                            : st.label}
                        </Badge>
                        {run.statusMessage && (
                          <p className="text-xs text-muted-foreground mt-1">{run.statusMessage}</p>
                        )}
                      </TableCell>
                      <TableCell>{run.eventsFound}</TableCell>
                      <TableCell>{run.eventsInserted}</TableCell>
                      <TableCell>{run.eventsSkipped}</TableCell>
                      <TableCell>
                        {hasSkipped ? (
                          <button
                            className="cursor-pointer"
                            onClick={() => toggleExpanded(run.id)}
                            title="Pokaż pominięte wydarzenia"
                          >
                            <Badge variant="secondary">{run.skippedEvents!.length}</Badge>
                          </button>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>{run.pagesVisited}</TableCell>
                      <TableCell>{run.llmTokensUsed.toLocaleString()}</TableCell>
                      <TableCell>
                        {run.engine === "codex" ? (
                          <span title="Zużycie tokenów nie określa procentu limitu abonamentowego">
                            Abonament Codex
                          </span>
                        ) : run.engine === "json_api" ? (
                          "Bez LLM"
                        ) : (
                          run.llmCostEstimate.toFixed(4)
                        )}
                      </TableCell>
                      <TableCell>
                        {hasErrors ? (
                          <button
                            className="flex items-center gap-1 cursor-pointer"
                            onClick={() => toggleExpanded(run.id)}
                            title="Pokaż szczegóły"
                          >
                            <Badge variant="destructive">{run.errors!.length}</Badge>
                            {isExpanded ? (
                              <ChevronDown className="h-3 w-3 text-muted-foreground" />
                            ) : (
                              <ChevronRight className="h-3 w-3 text-muted-foreground" />
                            )}
                          </button>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                    {hasDetails && isExpanded && (
                      <TableRow key={`${run.id}-details`} className="bg-muted/30">
                        <TableCell colSpan={11} className="py-2 px-4 space-y-3">
                          {hasErrors && (
                            <div>
                              <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">
                                Błędy ({run.errors!.length})
                              </p>
                              <ul className="space-y-1">
                                {run.errors!.map((err, i) => (
                                  <li
                                    key={i}
                                    className="text-xs text-destructive font-mono bg-destructive/10 rounded px-2 py-1"
                                  >
                                    {err.source && (
                                      <span className="font-semibold">
                                        [
                                        {sources?.find((source) => source.id === err.source)
                                          ?.name ?? err.source}
                                        ]{" "}
                                      </span>
                                    )}
                                    {err.message}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {hasSkipped && (
                            <div>
                              <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">
                                Pominięte wydarzenia ({run.skippedEvents!.length})
                              </p>
                              <ul className="space-y-1">
                                {run.skippedEvents!.map((ev, i) => (
                                  <li
                                    key={i}
                                    className="text-xs text-yellow-700 font-mono bg-yellow-500/10 rounded px-2 py-1"
                                  >
                                    <span className="font-semibold">
                                      [
                                      {sources?.find((source) => source.id === ev.source)?.name ??
                                        ev.source}
                                      ]
                                    </span>{" "}
                                    &quot;
                                    {ev.title}&quot; — {ev.reason}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableCard>
      )}
    </div>
  );
}
